/**
 * 分拣过程动画 —— 重建层（ProcessFrame[] → 渲染 Frame[]）
 *
 * 后端产「基准帧 + 增量帧」`ProcessFrame[]`（见 contract.md §1），本层按需重建渲染
 * `Frame`：持可变 workState，`frameAt(t)` = 取 ≤t 最近基准帧深拷贝 → 前放 (base,t] 增量
 * → 派生展示字段（caption/active/metrics/托盘聚合/dwellMs/keyFrame）；focus/decision 原样
 * 透传。Lazy：常驻仅一份 workState + wire 帧序列，不预物化全量 Frame[]（万件级可控）。
 *
 * - 前进/后退/seek 都走「回最近基准帧 + 前放」，不反向 apply（确定性，契约 §5）。
 * - 托盘有序别名：wire palletNo（UUID）按全序列首次出现顺序映 P001/P002…，所有引用一致翻译。
 * - `actionAt`/`phaseAt` 直接读 wire 元数据，供关键帧扫描/段落切分，不触发重建。
 */

import type {
  Active, Decision, FocusTarget, Frame, FrameChanges, FrameProvider, Item,
  ItemSnapshot, Metrics, Pallet, PalletShell, PalletStatus, Phase, ProcessFrame, StepAction,
} from './types';

/** 各动作建议停留时长（1x），对齐 contract.md §5 默认表 */
const DWELL_MS: Record<StepAction, number> = {
  init: 1000,
  'start-pallet': 500,
  'try-load': 1100,
  loaded: 450,
  'seal-pallet': 700,
  oversized: 1000,
  'merge-eval': 1100,
  'merge-pick': 600,
  'merge-absorb': 700,
  'merge-round-end': 800,
  final: 1200,
};

/** 关键帧动作（contract.md §5），供关键帧跳转 */
const KEY_FRAME_ACTIONS = new Set<StepAction>([
  'init', 'seal-pallet', 'oversized', 'merge-pick', 'merge-round-end', 'final',
]);

function isKeyFrameAction(a: StepAction): boolean {
  return KEY_FRAME_ACTIONS.has(a);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** wire 帧校验：非空、seq 单调连续、base 帧带 items/pallets、增量帧带 changes（任务 1.4） */
export function isValidProcessFrames(frames: unknown): frames is ProcessFrame[] {
  if (!Array.isArray(frames) || frames.length === 0) return false;
  let expect = 0;
  for (const f of frames) {
    if (!f || typeof f !== 'object') return false;
    const fr = f as Record<string, unknown>;
    if (fr.seq !== expect++) return false;
    if (typeof fr.phase !== 'string' || typeof fr.action !== 'string' || typeof fr.base !== 'boolean') return false;
    if (fr.base) {
      if (!Array.isArray(fr.items) || !Array.isArray(fr.pallets)) return false;
    } else if (typeof fr.changes !== 'object' || fr.changes === null) {
      return false;
    }
  }
  return true;
}

interface WorkItem {
  id: string;
  sku: string;
  markPrefix: string;
  markSerialNo: string;
  volume: number;
  weight: number;
  quantity?: number;
  palletNo?: string;
}

interface WorkPallet {
  palletNo: string;
  sku: string;
  maxVolume: number;
  maxWeight: number;
  status: PalletStatus;
}

interface Totals {
  weight: number;
  volume: number;
  count: number;
  marks: Set<string>;
}

/** 按全序列首次出现顺序构建 UUID → P00x 别名表（contract.md §3） */
function buildAliasMap(frames: ProcessFrame[]): Map<string, string> {
  const ordered: string[] = [];
  const seen = new Set<string>();
  const add = (no?: string | null): void => {
    if (no && !seen.has(no)) {
      seen.add(no);
      ordered.push(no);
    }
  };
  for (const f of frames) {
    if (f.base) {
      f.pallets?.forEach(p => add(p.palletNo));
      f.items?.forEach(it => add(it.palletNo));
    }
    const ch = f.changes;
    ch?.palletOpens?.forEach(p => add(p.palletNo));
    ch?.palletSeals?.forEach(add);
    ch?.palletRemoves?.forEach(add);
    ch?.itemMoves?.forEach(m => add(m.palletNo));
    const fo = f.focus;
    if (fo?.type === 'pallet') add(fo.palletNo);
    if (fo?.type === 'pair' && fo.pair) {
      add(fo.pair.target);
      add(fo.pair.source);
    }
  }
  const m = new Map<string, string>();
  ordered.forEach((no, i) => m.set(no, `P${String(i + 1).padStart(3, '0')}`));
  return m;
}

function deepCopyItem(s: ItemSnapshot): WorkItem {
  const it: WorkItem = {
    id: s.id, sku: s.sku, markPrefix: s.markPrefix, markSerialNo: s.markSerialNo,
    volume: s.volume, weight: s.weight,
  };
  if (s.quantity != null) it.quantity = s.quantity;
  if (s.palletNo != null) it.palletNo = s.palletNo;
  return it;
}

function deepCopyShell(s: PalletShell): WorkPallet {
  return { palletNo: s.palletNo, sku: s.sku, maxVolume: s.maxVolume, maxWeight: s.maxWeight, status: s.status };
}

/** 按 ProcessFrame[] 懒重建渲染 Frame 的帧提供者 */
export class Reconstructor implements FrameProvider {
  private readonly frames: ProcessFrame[];
  private readonly alias: Map<string, string>;
  private readonly baseIndices: number[];
  private readonly mergeStartCount: number;
  private items: WorkItem[] = [];
  private readonly itemsById = new Map<string, WorkItem>();
  private pallets: WorkPallet[] = [];
  private readonly palletsByNo = new Map<string, WorkPallet>();
  private curBase = -1;
  private curT = -1;

  constructor(frames: ProcessFrame[]) {
    this.frames = frames;
    this.alias = buildAliasMap(frames);
    this.baseIndices = frames
      .map((f, i) => (f.base ? i : -1))
      .filter(i => i >= 0);
    // 合并起点托盘数（首个 merge 基准帧的非 removed 盘数），seek 无关
    const mergeBase = frames.find(f => f.base && f.phase === 'merge');
    this.mergeStartCount = mergeBase?.pallets?.filter(p => p.status !== 'removed').length ?? 0;
  }

  get length(): number {
    return this.frames.length;
  }

  actionAt(t: number): StepAction | undefined {
    return this.frames[t]?.action;
  }

  phaseAt(t: number): Phase | undefined {
    return this.frames[t]?.phase;
  }

  frameAt(t: number): Frame | undefined {
    if (t < 0 || t >= this.frames.length) return undefined;
    this.ensureAt(t);
    return this.deriveFrame(this.frames[t]);
  }

  /** 把 workState 推进到帧 t（必要时回最近基准帧深拷贝 + 前放） */
  private ensureAt(t: number): void {
    const base = this.nearestBase(t);
    if (base !== this.curBase || t < this.curT) {
      this.loadBase(base);
    }
    for (let i = this.curT + 1; i <= t; i++) {
      this.applyChanges(this.frames[i].changes);
    }
    this.curT = t;
  }

  private nearestBase(t: number): number {
    let lo = 0;
    let hi = this.baseIndices.length - 1;
    let ans = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (this.baseIndices[mid] <= t) {
        ans = this.baseIndices[mid];
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return ans;
  }

  private loadBase(b: number): void {
    const f = this.frames[b];
    this.items = (f.items ?? []).map(deepCopyItem);
    this.itemsById.clear();
    for (const it of this.items) this.itemsById.set(it.id, it);
    this.pallets = (f.pallets ?? []).map(deepCopyShell);
    this.palletsByNo.clear();
    for (const p of this.pallets) this.palletsByNo.set(p.palletNo, p);
    this.curBase = b;
    this.curT = b;
  }

  private applyChanges(ch?: FrameChanges): void {
    if (!ch) return;
    if (ch.itemMoves) {
      for (const m of ch.itemMoves) {
        const it = this.itemsById.get(m.id);
        if (it) it.palletNo = m.palletNo ?? undefined;
      }
    }
    if (ch.palletOpens) {
      for (const sh of ch.palletOpens) {
        if (!this.palletsByNo.has(sh.palletNo)) {
          const p: WorkPallet = {
            palletNo: sh.palletNo, sku: sh.sku, maxVolume: sh.maxVolume,
            maxWeight: sh.maxWeight, status: sh.status,
          };
          this.pallets.push(p);
          this.palletsByNo.set(p.palletNo, p);
        }
      }
    }
    if (ch.palletSeals) {
      for (const no of ch.palletSeals) {
        const p = this.palletsByNo.get(no);
        if (p) p.status = 'sealed';
      }
    }
    if (ch.palletRemoves) {
      for (const no of ch.palletRemoves) {
        const p = this.palletsByNo.get(no);
        if (p) p.status = 'removed';
      }
    }
  }

  private aliasPallet(no?: string | null): string {
    return no ? (this.alias.get(no) ?? no) : '';
  }

  private itemLabel(id: string): string {
    const it = this.itemsById.get(id);
    return it ? `${it.markPrefix}/${it.markSerialNo}` : id;
  }

  private roundNumberOf(seq: number): number {
    let n = 0;
    for (let i = 0; i <= seq; i++) {
      if (this.frames[i].action === 'merge-round-end') n++;
    }
    return n;
  }

  /** 由 workState + wire 帧派生渲染 Frame（focus/decision 透传，其余派生） */
  private deriveFrame(wire: ProcessFrame): Frame {
    const action = wire.action;
    const phase = wire.phase;
    const items = this.deriveItems();
    const pallets = this.derivePallets();
    const liveCount = this.pallets.filter(p => p.status !== 'removed').length;
    const metrics = this.deriveMetrics(phase, liveCount);
    const active = this.deriveActive(wire);
    const caption = this.deriveCaption(wire, items, liveCount);
    const frame: Frame = {
      phase, action, items, pallets, metrics, caption,
      dwellMs: DWELL_MS[action] ?? 1000,
      keyFrame: isKeyFrameAction(action),
    };
    if (active) frame.active = active;
    if (wire.focus) frame.focus = this.aliasFocus(wire.focus);
    if (wire.decision) frame.decision = wire.decision as Decision;
    return frame;
  }

  private deriveItems(): Item[] {
    return this.items.map(it => {
      const item: Item = {
        id: it.id, markPrefix: it.markPrefix, markSerialNo: it.markSerialNo,
        sku: it.sku, volume: it.volume, weight: it.weight,
      };
      if (it.quantity != null) item.quantity = it.quantity;
      if (it.palletNo != null) item.palletNo = this.aliasPallet(it.palletNo);
      return item;
    });
  }

  private derivePallets(): Pallet[] {
    const totals = new Map<string, Totals>();
    for (const it of this.items) {
      if (!it.palletNo) continue;
      let t = totals.get(it.palletNo);
      if (!t) {
        t = { weight: 0, volume: 0, count: 0, marks: new Set<string>() };
        totals.set(it.palletNo, t);
      }
      t.weight += it.weight;
      t.volume += it.volume;
      t.count += 1;
      t.marks.add(it.markPrefix);
    }
    return this.pallets
      .filter(p => p.status !== 'removed')
      .map(p => {
        const t = totals.get(p.palletNo) ?? { weight: 0, volume: 0, count: 0, marks: new Set<string>() };
        const wu = p.maxWeight > 0 ? t.weight / p.maxWeight : 0;
        const vu = p.maxVolume > 0 ? t.volume / p.maxVolume : 0;
        const pallet: Pallet = {
          palletNo: this.aliasPallet(p.palletNo),
          sku: p.sku,
          totalQuantity: t.count,
          totalVolume: round2(t.volume),
          totalWeight: round2(t.weight),
          itemCount: t.count,
          maxWeight: p.maxWeight,
          maxVolume: p.maxVolume,
          utilization: {
            weight: round2(wu),
            volume: round2(vu),
            bottleneck: round2(Math.max(wu, vu)),
          },
          status: p.status,
          marks: [...t.marks],
        };
        return pallet;
      });
  }

  private deriveMetrics(phase: Phase, liveCount: number): Metrics {
    const m: Metrics = { palletCount: liveCount };
    if (phase === 'merge' || phase === 'final') {
      m.originalPalletCount = this.mergeStartCount;
      m.mergedPalletCount = liveCount;
      m.savingRatio = this.mergeStartCount > 0
        ? Math.round(((this.mergeStartCount - liveCount) / this.mergeStartCount) * 100)
        : 0;
    }
    return m;
  }

  /** active 派生（决策 4 规则表）；try-load 的盘号取自 workState 当前 open 盘 */
  private deriveActive(wire: ProcessFrame): Active | undefined {
    const f = wire.focus;
    const ch = wire.changes;
    const active: Active = {};
    switch (wire.action) {
      case 'start-pallet':
      case 'seal-pallet':
        if (f?.type === 'pallet' && f.palletNo) active.pallets = [this.aliasPallet(f.palletNo)];
        break;
      case 'try-load':
        if (f?.itemId) active.items = [f.itemId];
        active.pallets = this.pallets.filter(p => p.status === 'open').map(p => this.aliasPallet(p.palletNo));
        break;
      case 'loaded':
      case 'oversized':
        if (f?.itemId) active.items = [f.itemId];
        if (ch?.itemMoves?.[0]?.palletNo) active.pallets = [this.aliasPallet(ch.itemMoves[0].palletNo)];
        break;
      case 'merge-eval':
      case 'merge-pick':
        if (f?.type === 'pair' && f.pair) {
          active.pallets = [this.aliasPallet(f.pair.target), this.aliasPallet(f.pair.source)];
        }
        break;
      case 'merge-absorb': {
        const moved = ch?.itemMoves?.map(m => m.id) ?? [];
        if (moved.length) active.items = moved;
        if (f?.type === 'pallet' && f.palletNo) active.pallets = [this.aliasPallet(f.palletNo)];
        break;
      }
      default:
        return undefined;
    }
    if (!active.items && !active.pallets) return undefined;
    return active;
  }

  private aliasFocus(f: FocusTarget): FocusTarget {
    if (f.type === 'pallet' && f.palletNo) {
      return { ...f, palletNo: this.aliasPallet(f.palletNo) };
    }
    if (f.type === 'pair' && f.pair) {
      return {
        ...f,
        pair: {
          target: this.aliasPallet(f.pair.target),
          source: this.aliasPallet(f.pair.source),
          tightness: f.pair.tightness,
        },
      };
    }
    return f;
  }

  private deriveCaption(wire: ProcessFrame, items: Item[], liveCount: number): string {
    const f = wire.focus;
    const ch = wire.changes;
    const detailCount = items.filter(i => i.markSerialNo).length;
    switch (wire.action) {
      case 'init':
        if (wire.phase === 'raw') {
          const pieces = items.reduce((s, i) => s + (i.quantity ?? 1), 0);
          return `原始唛头：${items.length} 个唛头 / ${pieces} 件`;
        }
        if (wire.phase === 'expanded') return `明细展开：${detailCount} 件，开始分组装箱`;
        if (wire.phase === 'merge') return `进入合并：${liveCount} 个托盘`;
        return '初始状态';
      case 'start-pallet':
        return `新开托盘 ${this.aliasPallet(f?.palletNo)}`;
      case 'try-load': {
        const open = this.pallets.find(p => p.status === 'open');
        return `判断装入 ${f?.itemId ? this.itemLabel(f.itemId) : ''} → ${open ? this.aliasPallet(open.palletNo) : ''}`;
      }
      case 'loaded':
        return `装入 ${this.aliasPallet(ch?.itemMoves?.[0]?.palletNo)}`;
      case 'seal-pallet':
        return `${this.aliasPallet(f?.palletNo)} 封箱`;
      case 'oversized':
        return `${f?.itemId ? this.itemLabel(f.itemId) : ''} 单件超限，独占 ${this.aliasPallet(ch?.itemMoves?.[0]?.palletNo)}`;
      case 'merge-eval': {
        const p = f?.type === 'pair' ? f.pair : undefined;
        return p
          ? `评估 ${this.aliasPallet(p.target)} + ${this.aliasPallet(p.source)}（紧度 ${p.tightness.toFixed(2)}）`
          : '评估合并';
      }
      case 'merge-pick': {
        const p = f?.type === 'pair' ? f.pair : undefined;
        return p ? `选中 ${this.aliasPallet(p.target)} ← ${this.aliasPallet(p.source)}` : '选中合并';
      }
      case 'merge-absorb': {
        const moved = ch?.itemMoves?.length ?? 0;
        return `${this.aliasPallet(f?.palletNo)} 吸收 ${moved} 件`;
      }
      case 'merge-round-end':
        return `第 ${this.roundNumberOf(wire.seq)} 轮合并结束：剩 ${liveCount} 盘`;
      case 'final':
        return `最终方案：${liveCount} 个托盘，共 ${detailCount} 件`;
      default:
        return '';
    }
  }
}

/** 包一份已物化的 Frame[] 为 FrameProvider（mock 回退 / 测试用，不经重建） */
export class ArrayFrameProvider implements FrameProvider {
  private readonly frames: Frame[];
  constructor(frames: Frame[]) {
    this.frames = frames;
  }
  get length(): number {
    return this.frames.length;
  }
  frameAt(t: number): Frame | undefined {
    return this.frames[t];
  }
  actionAt(t: number): StepAction | undefined {
    return this.frames[t]?.action;
  }
  phaseAt(t: number): Phase | undefined {
    return this.frames[t]?.phase;
  }
}
